import {
  Stack,
  Typography,
  Dialog,
  Button,
  TextField,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  Box,
  InputLabel,
  Link,
  useTheme,
  Autocomplete
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { CustomerState, getTeamMembers, selectJobTitles } from '../../../redux/customer';
import { login, selectIsAdmin, selectIsAdminOrOwner, selectIsOwner, UserAccessLevels } from '../../../redux/user';
import { CustomerDetails, Group, GroupName, User } from '../../../types';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import constants from '../../../constants';
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../components/Dialog.styled';
import { hour, timezones } from './constants';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { ToolTip } from '../dentalenhancements/components/ToolTip';
import { WarningAlertBox } from '../../../components/AlertBoxes.styled';
import { addError } from '../../../redux/alerts';
import AuthService from '../../../services/authorization';
import CloseModalButton from '../../../components/CloseModalButton';
import { updateUser } from '../../../services/teamMemberService';
import { getJobTitle, validateJobTitle } from '../team-members/utils/helpers';
import { updateWaterlineMetadata } from '../../../services/waterlineService';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const changePasswordLink = `${constants.LOGIN_URL}/change-password`;

const initialFormDataState = {
  cellphone: '',
  email: '',
  default_customer: '',
  waterline_compliance_interval: '',
  reminder_time: '',
  firstname: '',
  middlename: '',
  lastname: '',
  position: '',
  otherPosition: '',
  email_reminder: false,
  email_bio_passed: false,
  email_bio_failed: false,
  email_bio_child_failed: false,
  email_waterline_passed: false,
  email_waterline_failed: false,
  email_waterline_child_failed: false,
  waterline_groups: [] as string[],
  biological_groups: [] as string[],
  timezone: ''
};

interface GroupPayload {
  category: string;
  group_id: number;
}

type SettingsModalProps = {
  open: boolean;
  handleClose: (success: boolean) => void;
  biologicalNotificationGroups: GroupName[];
  waterlineNotificationGroups: GroupName[];
  user: User;
  defaultCustomer?: CustomerDetails;
  customer?: CustomerState;
};

const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  handleClose,
  biologicalNotificationGroups,
  waterlineNotificationGroups,
  user,
  defaultCustomer,
  customer
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const jobTitles = useAppSelector(selectJobTitles)

  const fontStyles = {
    fontSize: '13px',
    fontFamily: "'Open Sans', sans-serif"
  };

  const StyledText = styled(Typography)(({ theme }) => fontStyles);

  const StyledSectionHeaders = styled(Box)(() => ({
    borderBottom: 1,
    borderColor: 'divider',
    marginTop: '15px',
    marginBottom: '15px'
  }));

  const StyledLink = styled(Link)(() => fontStyles);

  const [formData, setFormData] = useState(initialFormDataState);
  const [loading, setLoading] = useState(false);
  const [otherJobTitleError, setOtherJobTitleError] = useState('');

  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner);
  const isOwner = useAppSelector(selectIsOwner)
  const isAdmin = useAppSelector(selectIsAdmin);

  const validateOtherJobTitle = (otherJobTitle: string) => {
    const { message } = validateJobTitle(otherJobTitle);
    setOtherJobTitleError(message);
  };

  const handleEditChange = (field: string, value: string | string[] | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email: string) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    const digitsOnly = phoneNumber.replace(/-/g, '');
    return digitsOnly.length === 10 && /^\d+$/.test(digitsOnly);
  };

  const validateForm = () => {
    if (formData.email === '' || !validateEmail(formData.email)) {
      return false;
    }

    if (!!formData.cellphone && !validatePhoneNumber(formData.cellphone)) {
      return false;
    }

    if (formData.firstname === '' || !formData.firstname) {
      return false;
    }

    if (formData.lastname === '' || !formData.lastname) {
      return false;
    }

    if (formData.position === '' || !formData.position) {
      return false;
    }

    if (formData?.position.toLowerCase() === 'other' && (formData.otherPosition === '' || !formData.otherPosition || !!otherJobTitleError)) {
      return false;
    }

    return true;
  };

  const saveChanges = async () => {
    if (!customer) {
      return;
    }

    setLoading(true);

    const appUser: Partial<User> = {
      access_level: user?.access_level,
      account_active: user?.account_active,
      login_user_id: user?.login_user_id,
      firstname: formData?.firstname,
      middlename: formData?.middlename,
      lastname: formData?.lastname,
      email: formData?.email,
      customer_number: customer.customer_number,
      email_reminder: formData.email_reminder ? 1 : 0,
      position: formData.position.toLowerCase() === 'other' ? formData.otherPosition : formData.position,
      timezone: formData.timezone,
      reminder_time: formData.reminder_time,
      cellphone: formData.cellphone,
      default_customer_number: formData.default_customer,
      email_bio_passed: formData.email_bio_passed,
      email_bio_failed: formData.email_bio_failed,
      email_bio_child_failed: formData.email_bio_child_failed,
      email_waterline_passed: formData.email_waterline_passed,
      email_waterline_failed: formData.email_waterline_failed,
      email_waterline_child_failed: formData.email_waterline_child_failed,
      is_notification_team_member_editable: user.is_notification_team_member_editable
    };

    const groupMap = new Map<string, number>();
    customer?.groups?.forEach((group) => {
      groupMap.set(group.name, group.group_id);
    });

    const saveNotificationGroups: GroupPayload[] = [];

    formData.biological_groups.forEach((groupName) => {
      const groupId = groupMap.get(groupName);
      if (groupId) {
        saveNotificationGroups.push({
          group_id: groupId,
          category: 'biological'
        });
      }
    });

    formData.waterline_groups.forEach((groupName) => {
      const groupId = groupMap.get(groupName);
      if (groupId) {
        saveNotificationGroups.push({
          group_id: groupId,
          category: 'waterline'
        });
      }
    });

    const promise1 = updateUser(appUser, customer.customer_number);

    const promise2 = axios.put(`${constants.ONTRAQ_API_URL}/api2/${customer.customer_number}/user-notification-groups`, {
      user_id: user?.user_id,
      groups: saveNotificationGroups
    });

    const updateComplianceInterval = updateWaterlineMetadata(customer.customer_number, {
      waterline_compliance_interval: formData.waterline_compliance_interval
    })

    try {
      await Promise.all([promise1, promise2, updateComplianceInterval]);

      const authService = new AuthService();
      const currentUser = await authService.createSession();

      dispatch(login(currentUser));
      await dispatch(getTeamMembers(customer.customer_number));

      setLoading(false);
      handleClose(true);
      setFormData(initialFormDataState);
    } catch (err) {
      console.error(err);
      setLoading(false);
      dispatch(addError(`There was an error updating your account settings`));
      handleClose(false);
    }
  };

  const hasAssignedGroups = (assignedGroups: Group[] = []) => assignedGroups && assignedGroups.length > 0;

  useEffect(() => {
    if (customer && user && jobTitles.length) {
      const { title, otherTitle } = getJobTitle(user, jobTitles);

      setFormData({
        cellphone: user.cellphone || '',
        email: user.email || '',
        default_customer: defaultCustomer?.customer_number || '',
        waterline_compliance_interval: customer.customerMetadata?.waterline_compliance_interval ?? '',
        timezone: user?.timezone || '',
        reminder_time: user.reminder_time || '',
        firstname: user.firstname || '',
        middlename: user.middlename || '',
        lastname: user.lastname || '',
        position: title || '',
        otherPosition: otherTitle || '',
        email_reminder: !!user.email_reminder,
        email_bio_passed: !!user.email_bio_passed,
        email_bio_failed: !!user.email_bio_failed,
        email_bio_child_failed: !!user.email_bio_child_failed,
        email_waterline_passed: !!user.email_waterline_passed,
        email_waterline_failed: !!user.email_waterline_failed,
        email_waterline_child_failed: !!user.email_waterline_child_failed,
        waterline_groups: waterlineNotificationGroups.map(({ name }) => name) || ([] as string[]),
        biological_groups: biologicalNotificationGroups.map(({ name }) => name) || ([] as string[])
      });
    }
  }, [customer, user, defaultCustomer, open, jobTitles, biologicalNotificationGroups, waterlineNotificationGroups]);

  return (
    <>
      <Dialog open={open} onClose={() => handleClose(false)} maxWidth="sm" sx={{ color: theme.palette.color50555b.main }} fullWidth>
        <CloseModalButton handleClose={() => handleClose(false)} />
        <StyledDialogTitle>Edit Account Settings</StyledDialogTitle>
        <StyledDialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <StyledSectionHeaders>
              <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                Employee Information
              </Typography>
            </StyledSectionHeaders>
            <TextField
              size="small"
              label="First Name (required)"
              value={formData.firstname}
              onChange={(e) => handleEditChange('firstname', e.target.value)}
              error={!formData.firstname}
              helperText={!formData.firstname ? 'First name is required' : ''}
              fullWidth
            />
            <TextField
              size="small"
              label="Middle Name"
              value={formData.middlename}
              onChange={(e) => handleEditChange('middlename', e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Last Name (required)"
              value={formData.lastname}
              onChange={(e) => handleEditChange('lastname', e.target.value)}
              error={!formData.lastname}
              helperText={!formData.lastname ? 'Last name is required' : ''}
              fullWidth
            />
            <Autocomplete
              value={formData.position}
              onChange={(_, newValue) => handleEditChange('position', newValue ?? '')}
              options={jobTitles.map((jobTitle) => jobTitle.job_title)}
              isOptionEqualToValue={(option: string, value: string) => {
                return option.toLowerCase() === value.toLowerCase();
              }}
              getOptionLabel={(option) => option}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Job Title (required)"
                  error={!formData.position}
                  helperText={!formData.position ? 'Job Title is required' : ''}
                  fullWidth
                />
              )}
            />
            {formData.position === 'Other' ? (
              <TextField
                value={formData.otherPosition}
                size="small"
                label="Other Job Title (required)"
                onChange={(e) => {
                  handleEditChange('otherPosition', e.target.value);
                  validateOtherJobTitle(e.target.value);
                }}
                error={!formData.otherPosition || !!otherJobTitleError}
                helperText={!formData.otherPosition ? 'Other Job Title is required' : otherJobTitleError}
                fullWidth
              />
            ) : null}
            <TextField
              size="small"
              label="Phone Number"
              value={formData.cellphone}
              onChange={(e) => handleEditChange('cellphone', e.target.value)}
              fullWidth
              error={!!formData.cellphone && !validatePhoneNumber(formData.cellphone)}
              helperText={!!formData.cellphone && !validatePhoneNumber(formData.cellphone) ? 'Phone number must be 10 digits (hyphens allowed)' : ''}
            />
            <TextField
              size="small"
              label="Email Address"
              value={formData.email}
              onChange={(e) => handleEditChange('email', e.target.value)}
              fullWidth
              error={formData.email === '' || !validateEmail(formData.email)}
              helperText={formData.email === '' ? 'Email address is required' : !validateEmail(formData.email) ? 'Invalid email address' : ''}
              required
            />
            <Box>
              <StyledLink href={changePasswordLink} target="_blank" rel="noopener">
                Change Password
              </StyledLink>
            </Box>
            <hr />
            <StyledSectionHeaders>
              <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                Practice Information
              </Typography>
            </StyledSectionHeaders>
            <FormControl size="small">
              <InputLabel id="default-customer">Default Customer</InputLabel>
              <Select
                labelId="default-customer"
                label="Default Customer"
                value={formData.default_customer}
                onChange={(e) => handleEditChange('default_customer', e.target.value)}
                fullWidth
              >
                {user?.available_customers?.map((availableCustomer) => (
                  <MenuItem key={availableCustomer.customer_number} value={availableCustomer.customer_number}>
                    {availableCustomer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel id="waterline-interval">Waterline Compliance Interval</InputLabel>
              <Select
                labelId="waterline-interval"
                label="Waterline Compliance Interval"
                value={formData.waterline_compliance_interval}
                onChange={(e) => handleEditChange('waterline_compliance_interval', e.target.value)}
                fullWidth
              >
                <MenuItem value="">Select Interval</MenuItem>
                <MenuItem value="quarter">Quarterly</MenuItem>
                <MenuItem value="year">Annually</MenuItem>
              </Select>
            </FormControl>
            <hr />
            <StyledSectionHeaders>
              <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }} variant="subtitle2">
                <span>General Notification Preferences</span>
              </Typography>
            </StyledSectionHeaders>
            <StyledText sx={{ display: 'block' }}>
              OnTraq sends you reminders and notifications about things happening in your practice. Please tell us where you'd like to receive these
              messages:
            </StyledText>
            {!formData.email_reminder && (
              <WarningAlertBox sx={fontStyles}>
                Warning: Disabling a communication method may cause you to miss important notifications about tasks or other items pertaining to your
                practice.
              </WarningAlertBox>
            )}
            <FormGroup sx={{ padding: '7px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={formData?.email_reminder}
                    onChange={(e) => handleEditChange('email_reminder', e.target.checked)}
                  />
                }
                label={<StyledText>Send via email address</StyledText>}
              />
            </FormGroup>
            <hr />
            <StyledSectionHeaders>
              <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }} variant="subtitle2">
                <span className='m-r-8'>Biological Notification Preferences</span>
                <ToolTip helpText="As per your company policy, this setting is managed by the Owner/Admin" />
              </Typography>
            </StyledSectionHeaders>
            <FormGroup sx={{ padding: '7px' }}>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={formData?.email_bio_passed}
                    onChange={(e) => handleEditChange('email_bio_passed', e.target.checked)}
                    disabled={!user?.is_notification_team_member_editable && user?.access_level === UserAccessLevels.TeamMember}
                  />
                }
                label={
                  <StyledText>
                    Send me notifications when my practice receives a passed Biological test result
                  </StyledText>
                }
              />
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={formData?.email_bio_failed}
                    onChange={(e) => handleEditChange('email_bio_failed', e.target.checked)}
                    disabled={(!user?.is_notification_team_member_editable && user?.access_level === UserAccessLevels.TeamMember) || isOwner}
                  />
                }
                label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StyledText>Send me notifications when my practice receives a failed Biological test result </StyledText>
                  <ToolTip helpText="Owners will always receive a failed test result notification. Best practice is to add two more Team Members who will receive fail test results." />
                </Box>}
              />
              {isAdminOrOwner && customer?.corporate_parent && (
                <FormControlLabel
                  disabled={isAdmin && hasAssignedGroups(user?.available_groups) && user?.email_bio_child_failed}
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      checked={formData?.email_bio_child_failed}
                      onChange={(e) => handleEditChange('email_bio_child_failed', e.target.checked)}
                    />
                  }
                  label={
                    <StyledText>
                      Send me notifications when one of the practices in my organization receives a failed Biological test result
                    </StyledText>
                  }
                />
              )}
            </FormGroup>

            {isAdminOrOwner && formData.email_bio_child_failed && customer?.corporate_parent && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={customer?.groups.map((group) => group.name) || []}
                value={formData.biological_groups}
                onChange={(_, newValue) => handleEditChange('biological_groups', newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" label="Biological Notification Groups" />}
                fullWidth
                disabled={isAdmin && hasAssignedGroups(user?.available_groups) && biologicalNotificationGroups.length > 0}
              />
            )}
            <hr />
            <StyledSectionHeaders>
              <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                <span className='m-r-8'>Waterline Notification Preferences</span>
                <ToolTip helpText="As per your company policy, this setting is managed by the Owner/Admin" />
              </Typography>
            </StyledSectionHeaders>

            <FormGroup sx={{ padding: '7px' }}>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={formData?.email_waterline_passed}
                    onChange={(e) => handleEditChange('email_waterline_passed', e.target.checked)}
                    disabled={!user?.is_notification_team_member_editable && user?.access_level === UserAccessLevels.TeamMember}
                  />
                }
                label={
                  <StyledText>
                    Send me notifications when my practice receives a passed Waterline test result
                  </StyledText>
                }
              />
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={formData?.email_waterline_failed}
                    onChange={(e) => handleEditChange('email_waterline_failed', e.target.checked)}
                    disabled={(!user?.is_notification_team_member_editable && user?.access_level === UserAccessLevels.TeamMember) || isOwner}
                  />
                }
                label={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StyledText>Send me notifications when my practice receives a failed Waterline test result </StyledText>
                  <ToolTip helpText="Owners will always receive a failed test result notification. Best practice is to add two more Team Members who will receive fail test results." />
                </Box>}
              />
              {isAdminOrOwner && customer?.corporate_parent && (
                <FormControlLabel
                  disabled={isAdmin && hasAssignedGroups(user?.available_groups) && formData.email_waterline_child_failed}
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      checked={formData?.email_waterline_child_failed}
                      onChange={(e) => handleEditChange('email_waterline_child_failed', e.target.checked)}
                    />
                  }
                  label={
                    <StyledText>
                      Send me notifications when one of the practices in my organization receives a failed Waterline test result
                    </StyledText>
                  }
                />
              )}
            </FormGroup>

            {isAdminOrOwner && formData.email_waterline_child_failed && customer?.corporate_parent && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={customer?.groups.map((group) => group.name) || []}
                value={formData.waterline_groups}
                onChange={(_, newValue) => handleEditChange('waterline_groups', newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" label="Waterline Notification Groups" />}
                fullWidth
                disabled={isAdmin && hasAssignedGroups(user?.available_groups) && waterlineNotificationGroups.length > 0}
              />
            )}
            <hr />
            <StyledSectionHeaders>
              <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                Reminder and Time Zone Preferences
              </Typography>
            </StyledSectionHeaders>

            <FormControl size="small" fullWidth>
              <InputLabel id="timezone">Time Zone</InputLabel>
              <Select
                labelId="timezone"
                label="Time Zone"
                value={formData.timezone}
                onChange={(e) => handleEditChange('timezone', e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Time Zone
                </MenuItem>
                {timezones.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="reminder-time">Reminder Time</InputLabel>
              <Select
                labelId="reminder-time"
                label="Reminder Time"
                value={formData.reminder_time}
                onChange={(e) => handleEditChange('reminder_time', e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Reminder Time
                </MenuItem>
                {hour.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </StyledDialogContent>
        <StyledDialogFooter>
          <Button variant="outlined" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button disabled={loading || !validateForm()} variant="contained" onClick={saveChanges}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  );
};

export default SettingsModal;
